import { useContext, useEffect, useRef } from 'react';
import {
    TyreRepairRequest,
    TyreDamageTypeOption,
    TyreSpecificErrors,
    nonUSTyreDamageTypes,
    defaultTyreDamageTypes
} from './TyreRepairRequestTypes';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import SelectInput from '../../common/selectInput/SelectInput';
import FormInput from '../../common/FormInput';
import { useTranslation } from 'react-i18next';
import { TranslationHelper } from 'classes/TranslationHelper';
import DateInput from '../../common/dateInput/DateInput';
import { TyreRepairRequestContext } from '../../../context/TyreRepairRequestContext';
import FormImageUploader from 'components/common/fileUpload/FormImageUploader';
import Images from '../../../images/TyreSpecificFormImages';
import {
    SelectOptionsYesNo,
    TyreRepairOptions,
    UploadImageTypes,
    YesNo
} from '../../../Constants/Common';
import { UploadedDocument, UploadedImage } from '../../common/CommonTypes';
import {
    documentToImageFormat,
    imageByType,
    imageToDocumentFormat
} from '../../common/helper';
import DocumentUploader from '../../common/fileUpload/DocumentUploader';
import TextArea from '../../common/textArea/TextArea';
import { CommonValidation } from 'components/common/commonValidation/commonValidation';
import TextInput from '../../common/textInput/TextInput';
import { useAppSelector } from 'state/hooks';
import { selectIsUkSite, selectIsUSSite } from 'state/selectors/site';

export type TyreSpecificFormProps = {
    repairRequest: TyreRepairRequest;
    generalRequestDetails: {
        repairOption: string;
        hasBeenRepaired: YesNo;
        hasBeenPayed?: YesNo;
    };
    formErrors?: TyreSpecificErrors;
    onChange: (key: keyof TyreRepairRequest, value: string) => void;
    onChangeDamageType: (selectionName: string) => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onInvoiceChange: (invoice: UploadedDocument) => void;
    errorSummaryTitle: string;
    mileageAndRegistrationAlreadyUploaded: boolean;
};

const TyreSpecificForm = ({
    repairRequest,
    generalRequestDetails,
    formErrors,
    onChange,
    onUploadImageChange,
    onInvoiceChange,
    errorSummaryTitle,
    mileageAndRegistrationAlreadyUploaded
}: TyreSpecificFormProps): JSX.Element => {
    const { t } = useTranslation();
    const isNonUsSite = !useAppSelector(selectIsUSSite);
    const isUkSite = useAppSelector(selectIsUkSite);
    const helpTextDistance = t('tyre_repair_request.help_text.tread');
    const helpTextMileage = t('tyre_repair_request.help_text.mileage_image');
    const helpTextRegistration = t(
        'tyre_repair_request.help_text.registration_image'
    );
    const { onChangDateOfIncident } = useContext(TyreRepairRequestContext);

    const showImageSection =
        generalRequestDetails.repairOption !== TyreRepairOptions.Dealer;

    const showInvoice =
        generalRequestDetails.repairOption !== TyreRepairOptions.Dealer &&
        generalRequestDetails.hasBeenRepaired === SelectOptionsYesNo.Yes &&
        generalRequestDetails.hasBeenPayed === SelectOptionsYesNo.Yes;

    const showTyreRepairNoPayReason =
        isUkSite &&
        generalRequestDetails.hasBeenRepaired === SelectOptionsYesNo.Yes &&
        generalRequestDetails.hasBeenPayed === SelectOptionsYesNo.No;

    const onChangeEvent = (
        selectedValue: string,
        key: keyof TyreRepairRequest
    ) => {
        onChange(key, selectedValue);
    };

    const modalBottom = useRef(null);

    useEffect(() => {
        if (formErrors !== undefined && Object.keys(formErrors).length !== 0) {
            modalBottom.current.focus();
        }
    }, [formErrors]);

    const getTyreDamageOptions = (): TyreDamageTypeOption[] => {
        const tyreOptions: TyreDamageTypeOption[] = [];

        const damageTypesToConsider = isNonUsSite
            ? nonUSTyreDamageTypes
            : defaultTyreDamageTypes;

        Object.keys(damageTypesToConsider).forEach((tyreDamage) => {
            const option = {
                id: damageTypesToConsider[tyreDamage],
                text: TranslationHelper.getTranslatedText(
                    damageTypesToConsider[tyreDamage]
                ),
                value: damageTypesToConsider[tyreDamage],
                disabled: false
            };
            tyreOptions.push(option);
        });

        return tyreOptions;
    };

    const tyreDamageTypeOptions: TyreDamageTypeOption[] =
        getTyreDamageOptions();

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    const errorList = (): Array<string | undefined> => {
        return Object.values(formErrors).filter(Boolean);
    };

    const onChangeDate = (selectedIncidentDate: string): void => {
        if (
            selectedIncidentDate &&
            !CommonValidation.isDateNull(selectedIncidentDate, true)
        ) {
            onChangDateOfIncident(selectedIncidentDate);
        }
    };

    const onInvoiceUpdate = (_, __, image: UploadedImage) => {
        onInvoiceChange(imageToDocumentFormat(image));
    };

    const onChangeNoPaymentReason = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        onChange('noPaymentReason', event.target.value);
    };

    return (
        <div className='card page-content no-border'>
            <div className='card-body page-content-body'>
                <FormInput
                    id='incident-date-forminput'
                    label={t('incident_date')}
                    errorText={formErrors?.dateOfIncident}>
                    <DateInput
                        name='dateOfIncidentDatePicker'
                        useJson
                        forceTimeReset
                        value={repairRequest?.dateOfIncident}
                        onDateChanged={(dateString) => onChangeDate(dateString)}
                        maximumDate={maxDate()}
                    />
                </FormInput>
                <FormInput
                    id='damage-type-forminput'
                    label={t('tyre_repair_request.step_2_damage_type_title')}
                    helpText={t('help_text.size_of_damage')}
                    errorText={
                        formErrors?.damageType ?? formErrors?.damageTypeComment
                    }>
                    <SelectInput
                        name='damage-size'
                        loadingText={t('loading')}
                        value={repairRequest?.damageType}
                        options={tyreDamageTypeOptions}
                        onChange={(selectedValue: string) =>
                            onChangeEvent(selectedValue, 'damageType')
                        }
                        forceDefault
                        defaultIfOneOption
                        addDefaultOption
                        defaultText={t('select_default')}
                    />
                    {isNonUsSite &&
                        repairRequest?.damageType ===
                            nonUSTyreDamageTypes.Other && (
                            <TextArea
                                value={repairRequest?.damageTypeComment}
                                name='comment'
                                containerClassName='mt-3'
                                innerClassName='fullscreen'
                                label={t('please_state')}
                                maxLength={1000}
                                onChange={(e) =>
                                    onChangeEvent(
                                        e.currentTarget.value,
                                        'damageTypeComment'
                                    )
                                }
                            />
                        )}
                </FormInput>
            </div>
            {isNonUsSite && (
                <>
                    {showInvoice && (
                        <FormInput
                            id='invoice-form-input'
                            label={t(
                                'tyre_repair_request.invoice_upload_title'
                            )}
                            helpText={t(
                                'tyre_repair_request.help_text.invoice'
                            )}
                            errorText={formErrors?.invoice}>
                            <DocumentUploader
                                id={'invoice'}
                                documentIndex={0}
                                documentName={t(
                                    'tyre_repair_request.invoice_document_name'
                                )}
                                uploadedDocument={documentToImageFormat(
                                    repairRequest?.invoice
                                )}
                                saveOnUpload={true}
                                onDocumentUploaded={onInvoiceUpdate}
                                displayFilenameAsName
                            />
                        </FormInput>
                    )}

                    {showTyreRepairNoPayReason && (
                        <div>
                            <FormInput
                                id='tyre-repair-no-pay-reason-forminput'
                                label={t('tyre_repair_nopay_reason')}
                                errorText={formErrors?.noPaymentReason}>
                                <TextArea
                                    value={repairRequest?.noPaymentReason}
                                    name='tyre-repair-no-pay-reason'
                                    containerClassName='mt-3'
                                    innerClassName='fullscreen'
                                    maxLength={1000}
                                    onChange={onChangeNoPaymentReason}
                                />
                            </FormInput>
                        </div>
                    )}

                    {repairRequest?.damageType ===
                        nonUSTyreDamageTypes.Malicious &&
                        isUkSite && (
                            <FormInput
                                id='crime-reference-number'
                                label={t(
                                    'tyre_repair_request.crime_reference_number_title'
                                )}
                                helpText={t(
                                    'tyre_repair_request.help_text.crime_reference_number'
                                )}
                                errorText={formErrors?.crimeReferenceNumber}>
                                <TextInput
                                    value={repairRequest?.crimeReferenceNumber}
                                    name='crime'
                                    label={t('please_state')}
                                    maxLength={100}
                                    onChange={(e) =>
                                        onChangeEvent(
                                            e.currentTarget.value,
                                            'crimeReferenceNumber'
                                        )
                                    }
                                />
                            </FormInput>
                        )}
                    {showImageSection && (
                        <>
                            <FormInput
                                id='closeup-image-forminput'
                                label={t(
                                    'tyre_repair_request.step_2_damage_upload_photo_title'
                                )}
                                helpText={t(
                                    'tyre_repair_request.help_text.close_up'
                                )}
                                errorText={formErrors?.closeUpImage}>
                                <FormImageUploader
                                    id='close-up-damage'
                                    name='close up image of tyre damage'
                                    label={t(
                                        'tyre_repair_request.step_2_damage_upload_labels.close_up_image'
                                    )}
                                    image={repairRequest?.uploadedImages.find(
                                        imageByType(UploadImageTypes.CloseUp)
                                    )}
                                    exampleImage={{
                                        id: 'close-up-example-images',
                                        src: Images.TyreCloseup,
                                        label: t(
                                            'tyre_repair_request.step_2_damage_upload_labels.example'
                                        )
                                    }}
                                    onImageChange={onUploadImageChange}
                                />
                            </FormInput>
                            <FormInput
                                id='distance-image-forminput'
                                label={t(
                                    'tyre_repair_request.upload_image_tread_label'
                                )}
                                helpText={helpTextDistance}
                                errorText={formErrors?.distanceImage}>
                                <FormImageUploader
                                    id='distance-damage'
                                    name='image of tread'
                                    label={t(
                                        'tyre_repair_request.step_2_damage_upload_labels.image_of_tread'
                                    )}
                                    image={repairRequest?.uploadedImages.find(
                                        imageByType(UploadImageTypes.Distance)
                                    )}
                                    exampleImage={{
                                        id: 'damage-example-images',
                                        src: Images.TyreTread,
                                        label: t(
                                            'tyre_repair_request.step_2_damage_upload_labels.example'
                                        )
                                    }}
                                    onImageChange={onUploadImageChange}
                                />
                            </FormInput>
                            {isUkSite &&
                                generalRequestDetails.repairOption !==
                                    TyreRepairOptions.Dealer &&
                                !mileageAndRegistrationAlreadyUploaded && (
                                    <>
                                        <FormInput
                                            id='mileage-image-forminput'
                                            label={t(
                                                'tyre_repair_request.mileage_image_title'
                                            )}
                                            helpText={helpTextMileage}
                                            errorText={
                                                formErrors?.mileageImage
                                            }>
                                            <FormImageUploader
                                                id='mileage-image'
                                                name='image of a car speedometer'
                                                label={t(
                                                    'tyre_repair_request.mileage_image_title'
                                                )}
                                                image={repairRequest?.uploadedImages.find(
                                                    imageByType(
                                                        UploadImageTypes.Mileage
                                                    )
                                                )}
                                                exampleImage={{
                                                    id: 'mileage-example-images',
                                                    src: Images.Mileage,
                                                    label: t(
                                                        'tyre_repair_request.step_2_damage_upload_labels.example'
                                                    )
                                                }}
                                                onImageChange={
                                                    onUploadImageChange
                                                }
                                            />
                                        </FormInput>
                                        <FormInput
                                            id='registration-image-forminput'
                                            label={t(
                                                'tyre_repair_request.registration_image_title'
                                            )}
                                            helpText={helpTextRegistration}
                                            errorText={
                                                formErrors?.registrationImage
                                            }>
                                            <FormImageUploader
                                                id='registration-image'
                                                name='image of registration'
                                                label={t(
                                                    'tyre_repair_request.registration_image_title'
                                                )}
                                                image={repairRequest?.uploadedImages.find(
                                                    imageByType(
                                                        UploadImageTypes.Registration
                                                    )
                                                )}
                                                exampleImage={{
                                                    id: 'registration-example-images',
                                                    src: Images.Registration,
                                                    label: t(
                                                        'tyre_repair_request.step_2_damage_upload_labels.example'
                                                    )
                                                }}
                                                onImageChange={
                                                    onUploadImageChange
                                                }
                                            />
                                        </FormInput>
                                    </>
                                )}
                        </>
                    )}
                </>
            )}
            <div className='card-footer page-content-footer'>
                {formErrors && (
                    <ErrorSummary
                        title={errorSummaryTitle}
                        errors={errorList()}
                        className='card-error'
                    />
                )}
            </div>
            <div tabIndex={0} ref={modalBottom}></div>
        </div>
    );
};

export default TyreSpecificForm;
